import uuidv4 from "uuid/v4";
import * as FileSaver from "file-saver";
import Papa from "papaparse";
import Big from "big.js";
import { fn } from "util";
import { ProductRepository, StockOperationsRepository } from "core/repository";
import { Base2Presenter } from "core/presenter";

class InventoryAdjustPresenter extends Base2Presenter {
  initialState() {
    return {
      loading: true,
      resultSet: [],
      confirmation: {},
      counting: {},
      pagination: {
        perPage: 25,
        currentPage: 1,
        total: 0
      },
      order: {
        field: "name",
        direction: "ASC"
      },
      transform: [{ field: "amount", operation: "SUM" }],
      stockCountOpen: false,
      upload: {
        status: "absent"
      }
    };
  }

  setView(view) {
    this.view = view;
  }

  setUpload = value => {
    this.enqueue(vm => ({
      upload: {
        ...vm.upload,
        status: value
      }
    }));
  };

  async onStart() {
    this.enqueue({
      filter: {
        type: "all",
        code: "",
        name: ""
      }
    });
    this.doLoadResults();
  }

  onGoBack() {
    this.navigateTo("app.registration.product");
  }

  async doLoadResults() {
    this.enqueue({ loading: true });
    const productResults = await ProductRepository.getListOnline({
      order: [this.viewModel.order],
      pagination: {
        page: this.viewModel.pagination.currentPage,
        perPage: this.viewModel.pagination.perPage
      },
      filters: await this.getFilter()
    });

    if (productResults.success) {
      this.enqueue(vm => ({
        resultSet: productResults.data,
        pagination: {
          ...vm.pagination,
          total: productResults.total,
          perPage: productResults.perPage
        }
      }));
    } else {
      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content:
          "Não foi possível carregar os dados. Tente novamente mais tarde."
      });
    }

    this.enqueue({ loading: false });
  }

  async getFilter() {
    const { type, code, name } = this.viewModel.filter;
    const currentUser = await this.mRuntime.get("currentUser");

    const repositoryFilters = [
      {
        field: "company_uuid",
        operation: "EQ",
        value: currentUser.company_uuid
      },
      { field: "deleted", operation: "EQ", value: "false" },
      { field: "type", operation: "EQ", value: "product" }
    ];

    if (!fn.isBlank(code)) {
      repositoryFilters.push({ field: "code", operation: "LIKE", value: code });
    }

    if (!fn.isBlank(name)) {
      repositoryFilters.push({ field: "name", operation: "LIKE", value: name });
    }

    if (type !== "all") {
      const operation = { positive: "GT", zero: "EQ", negative: "LT" }[type];
      repositoryFilters.push({ field: "stock_count", operation, value: "0" });
    }

    return repositoryFilters;
  }

  async onFilterChange(filter) {
    this.enqueue(
      vm => ({
        filter,
        pagination: {
          ...vm.pagination,
          currentPage: 1
        }
      }),
      () => this.doLoadResults()
    );
  }

  async onPageChange(page) {
    this.enqueue(
      vm => ({
        pagination: {
          ...vm.pagination,
          currentPage: page
        }
      }),
      () => this.doLoadResults()
    );
  }

  async onOrderChange(field, direction) {
    this.enqueue(
      vm => ({
        order: {
          field,
          direction
        },
        pagination: {
          ...vm.pagination,
          currentPage: 1
        }
      }),
      () => this.doLoadResults()
    );
  }

  async getCurrentStock(result) {
    let stockResult = result.stock_count;

    const stockResults = await StockOperationsRepository.create({
      order: [{ field: "created_at", direction: "DESC" }],
      pagination: {
        page: this.viewModel.pagination.currentPage,
        perPage: this.viewModel.pagination.perPage
      },
      filters: [{ field: "sellable_uuid", value: result.uuid, operation: "EQ" }]
    });

    if (stockResults.success && stockResults.data.length > 0) {
      const data = stockResults.data[0];
      let result = Big(0);

      switch (data.type) {
        case "out":
          result = Big(data.current_stock).minus(data.amount);
          break;
        case "in":
          result = Big(data.current_stock).plus(data.amount);
          break;
        case "set":
          result = Big(data.amount);
          break;
        default:
          break;
      }

      stockResult = result.toString();
    }

    return stockResult;
  }

  async onStockModify() {
    this.enqueue({ loading: true });

    const { counting } = this.viewModel;
    const isCountingEmpty =
      Object.keys(counting).length === 0 && counting.constructor === Object;

    if (isCountingEmpty) {
      this.enqueue({ loading: false });
      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content: "Preencha a contagem de ao menos um produto para continuar."
      });
      return;
    }

    for (let key in counting) {
      const result = await StockOperationsRepository.create({
        activity: "inventory",
        uuid: uuidv4(),
        sellable_uuid: key,
        type: "set",
        note: "Ajuste de Inventário",
        amount: parseInt(counting[key], 10)
      });

      if (result.fatal || !result.success) {
        this.sendNotification({
          type: "error",
          title: "Ocorreu um erro",
          content:
            "Nosso serviço encontra-se indisponível. Tente novamente mais tarde."
        });
        this.enqueue({ loading: false });
        return;
      }
    }
    this.enqueue({ loading: false });
    this.sendNotification({
      type: "success",
      content: "Ajuste de inventário realizado com sucesso."
    });
    this.navigateTo("app.registration.product");
  }

  async onDownload() {
    this.enqueue({ loading: true });

    const repositoryResult = await ProductRepository.getInventoryCsv({
      order: [this.viewModel.order],
      filters: await this.getFilter()
    });

    if (!repositoryResult.success) {
      this.sendNotification({
        type: "error",
        title: "Ooopss...",
        content:
          "Não foi possível gerar o download do seu arquivo. Por favor, tente novamente mais tarde.\nSe o problema persistir, entre em contato com o suporte."
      });

      return;
    }

    const content = repositoryResult.data;
    const contentBlob = new Blob([content], {
      type: "application/csv;charset=utf-8"
    });
    FileSaver.saveAs(contentBlob, "contagem-de-estoque.csv");

    this.enqueue({ loading: false });
  }

  handleCountingChange(productUuid, value) {
    this.enqueue(vm => ({
      counting: {
        ...vm.counting,
        [productUuid]: value
      }
    }));
  }

  async onFileSelect(file) {
    if (file.type !== "text/csv") {
      const excelFile = file.type === "application/vnd.ms-excel";
      const endsWithCsv = file.name.indexOf(".csv") === file.name.length - 4;

      if (!(excelFile && endsWithCsv)) {
        this.setUpload("invalid");
        return;
      }
    }
    this.enqueue({ loading: true });

    Papa.parse(file, {
      complete: async results => {
        const data = results.data;

        const promises = data.map(async row => {
          const code = row[0];
          const name = row[1];
          let value;

          if (!row[4]) {
            return;
          }

          try {
            value = Big(row[4]);
          } catch (e) {
            return;
          }

          await this._parseConfirmation(code, name, value);
        });

        await Promise.all(promises);

        const status = "valid";
        this.setUpload(status);

        this.enqueue({ loading: false });
      },
      error: () => {
        this.setUpload("invalid");
        this.enqueue({ loading: false });
      }
    });
  }

  async _parseConfirmation(code, name, value) {
    const product = await ProductRepository.findFirst({
      filters: [
        { field: "code", operation: "EQ", value: code },
        { field: "name", operation: "EQ", value: name },
        ...(await this.getFilter())
      ]
    });

    if (product) {
      this.enqueue(vm => ({
        confirmation: {
          ...vm.confirmation,
          [product.uuid]: value.toFixed(2)
        }
      }));
    }
  }

  clearInformation() {
    this.enqueue({
      confirmation: {},
      counting: {}
    });
  }

  onImportStockCount() {
    this.enqueue({ stockCountOpen: true });
  }

  onImportStockCountClose() {
    this.enqueue({ stockCountOpen: false });
  }

  onImportStockCountConfirmation() {
    const { confirmation } = this.viewModel;
    this.clearInformation();
    const { status } = this.viewModel.upload;
    if (status === "valid") {
      this.enqueue(vm => ({
        counting: {
          ...vm.counting,
          ...confirmation
        }
      }));

      this.onImportStockCountClose();

      if (this.viewModel.counting && this.viewModel.upload.status === "valid") {
        this.sendNotification({
          type: "success",
          content: `Planilha carregada com sucesso.`
        });
      }
    } else {
      this.clearInformation();
      this.onImportStockCountClose();
    }
  }
}

export default InventoryAdjustPresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/product/inventory/adjust/presenter.js